import { useEffect, useState } from 'react';
import { marquerAccueilVu } from './api';
import { LIEN_RACCOURCI_IOS, estUnAppareilApple } from './raccourciIOS';
import { rafraichirSession, useSession } from './session';
import { Button, Icon, LogoLockup, buttonClasses } from './ui';

/**
 * Les premiers pas (US-104).
 *
 * **Le trou que cette story ferme.** Quelqu'un qui accepte une invitation
 * (US-103) arrive sur une bibliothèque vide, dans l'onglet d'un navigateur,
 * sans savoir que l'application s'installe, que le mode cuisine existe, ni que
 * ses recettes sont privées par défaut. Tout ce qui a été construit depuis la
 * vague E est invisible à qui n'était pas là pendant.
 *
 * **Une carte par idée, une phrase par idée** — six ou sept selon l'appareil. Pas une visite
 * guidée : les bulles posées sur l'interface réelle sont chères, fragiles à
 * chaque refonte, et détestées. Une pile qu'on fait défiler se lit en trente
 * secondes et se saute en une tape.
 *
 * **Une seule fois, et sur tous les appareils.** Le repère vit en base
 * (`member.welcomed_at`, migration `032`) et non dans `localStorage`, que
 * `memberScope.ts` purge au changement de membre et qui ne connaît qu'un
 * appareil à la fois.
 *
 * **Jamais bloquant.** « Passer » vaut « vu » — c'est la règle d'US-94 pour
 * l'invite d'installation, et elle vaut ici : un écran qu'on ne peut pas
 * fermer sans le lire est un écran qu'on apprend à fermer sans le lire.
 */

/** L'application tourne-t-elle déjà depuis l'écran d'accueil ? */
function dejaInstallee(): boolean {
  if (typeof window === 'undefined') return false;
  // `navigator.standalone` est le seul signal sur iOS, qui ne connaît pas
  // `display-mode: standalone` avant Safari 17 — et une bonne part des
  // téléphones de la maison sont en dessous.
  const iOS = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  if (iOS) return true;
  // **`matchMedia` n'existe pas partout** : jsdom ne l'implémente pas, et une
  // fonction d'affichage qui jette au montage emporterait l'écran des
  // réglages entier — ce que douze cas de `reglages.test.tsx` ont montré à la
  // première exécution. Le repli est « pas installée », donc la carte
  // s'affiche : montrer une fois de trop comment installer coûte moins cher
  // que ne jamais le montrer.
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * Le geste d'installation dépend du téléphone, et il n'y a pas d'API pour le
 * demander : `beforeinstallprompt` n'existe pas sur iOS, qui est justement le
 * cas où l'utilisateur ne devinera jamais tout seul. Reniflage d'`userAgent`
 * assumé — il ne décide de rien d'autre que d'une phrase.
 */
function gesteDInstallation(): string {
  if (typeof window === 'undefined') return '';
  const ua = window.navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) {
    return 'Touchez le bouton Partager, en bas de Safari, puis « Sur l’écran d’accueil ».';
  }
  return 'Ouvrez le menu du navigateur, puis « Installer l’application » ou « Ajouter à l’écran d’accueil ».';
}

interface Carte {
  titre: string;
  texte: string;
  /** Faux quand la carte n'a rien à apprendre à cet appareil-ci. */
  utile?: boolean;
  /** Une action, quand la carte en appelle une. Une seule, jamais deux. */
  lien?: { href: string; libelle: string };
}

function cartes(): Carte[] {
  return [
    {
      titre: 'Mettez-le sur votre écran d’accueil',
      texte: `${gesteDInstallation()} Sans cela, l’écran s’éteint pendant que vous cuisinez, et la barre du navigateur mange le quart de la page.`,
      utile: !dejaInstallee(),
    },
    {
      titre: 'Ajoutez une recette en la collant',
      texte:
        'Un lien, une photo, un texte copié : l’application lit et vous propose une fiche, que vous corrigez avant '
        + 'de l’enregistrer. Ce qui n’est pas écrit dans la source n’est jamais inventé — une quantité absente '
        + 'reste absente.',
    },
    {
      // **Instagram est la première source de recettes de la maison** (vague G),
      // et sur iPhone c'est le seul chemin : une application installée depuis
      // Safari ne peut pas s'inscrire dans la feuille de partage, là où
      // Android le permet nativement. Le raccourci comble ce trou, et il ne
      // s'installe pas tout seul — d'où une carte, et non une ligne dans la
      // précédente.
      titre: 'Depuis Instagram, en deux tapes',
      texte: estUnAppareilApple()
        ? 'Installez le raccourci « Ajouter à Mijoté », puis collez-y votre jeton personnel — Réglages, '
          + '« Depuis mon téléphone ». Ensuite, sur un Reel : Partager, Ajouter à Mijoté, et la recette vous '
          + 'attend dans le sas.'
        : 'Sur Android, partagez la publication vers Mijoté depuis le menu de partage : l’application y '
          + 'apparaît une fois installée sur l’écran d’accueil. La recette vous attend dans le sas.',
      lien: estUnAppareilApple()
        ? { href: LIEN_RACCOURCI_IOS, libelle: 'Installer le raccourci' }
        : undefined,
    },
    {
      titre: 'Gardée pour vous, ou dans le livre de famille',
      texte:
        'Chaque recette est à vous par défaut. Une bascule la met dans le livre commun, et seule la recette y '
        + 'passe : vos notes personnelles et votre « la prochaine fois » restent à vous.',
    },
    {
      titre: 'Le mode cuisine',
      texte:
        'Les étapes en grand, les minuteurs qui suivent d’un écran à l’autre, l’écran qui ne s’éteint pas, et les '
        + 'quantités qui s’adaptent au nombre de convives.',
    },
    {
      titre: 'La semaine, les courses, et ce que vous avez déjà',
      texte:
        'Posez des repas sur la semaine : la liste de courses s’en déduit. Et quand vous ne savez pas quoi faire, '
        + '« chercher avec ce que j’ai » part du frigo plutôt que d’une envie.',
    },
    {
      titre: 'Une chose à savoir',
      texte:
        'Vos recettes privées et votre journal ne sont visibles d’aucun autre membre. La personne qui héberge '
        + 'cette application, elle, a accès au fichier de données, donc à tout. Aucune application ne peut '
        + 'promettre le contraire.',
    },
  ].filter((carte) => carte.utile !== false);
}

export interface BienDemarrerProps {
  /** Appelée quand la personne ferme l'écran, par « Passer » ou par « J'ai compris ». */
  onFermer: () => void;
}

/** La pile de cartes, sans le décor : réutilisée par les réglages. */
export function BienDemarrerCartes(): React.JSX.Element {
  const liste = cartes();
  return (
    <ol className="flex flex-col gap-3">
      {liste.map((carte, index) => (
        <li key={carte.titre} className="flex gap-3 rounded-card border border-border bg-surface-raised p-4">
          <span
            aria-hidden="true"
            className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary-soft text-data text-sm font-semibold text-primary"
          >
            {index + 1}
          </span>
          <span className="flex min-w-0 flex-col gap-1">
            <span className="text-heading-3">{carte.titre}</span>
            <span className="text-sm text-foreground-secondary">{carte.texte}</span>
            {/* `rel` complet : un lien qui sort de l'application ne doit pas
                donner à la page d'arrivée la main sur celle-ci. */}
            {carte.lien !== undefined && (
              <a
                href={carte.lien.href}
                target="_blank"
                rel="noreferrer noopener"
                className={buttonClasses('secondary', 'compact') + ' mt-1 self-start'}
              >
                {carte.lien.libelle}
              </a>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * L'écran plein, affiché une fois.
 *
 * Un écran et non une fenêtre modale : sur un téléphone, une boîte flottante
 * de six cartes se réduit à un rectangle qui défile dans un autre rectangle
 * qui défile. `z-[70]` : au-dessus de la navigation (`z-50`) et du bandeau de
 * minuteurs (`z-[60]`), qui n'ont rien à faire ici — mais un minuteur en cours
 * pendant qu'on lit l'accueil ne devrait tout simplement pas arriver.
 */
export function BienDemarrer({ onFermer }: BienDemarrerProps): React.JSX.Element {
  useEffect(() => {
    function surTouche(evenement: KeyboardEvent): void {
      if (evenement.key === 'Escape') onFermer();
    }
    window.addEventListener('keydown', surTouche);
    return () => { window.removeEventListener('keydown', surTouche); };
  }, [onFermer]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bien démarrer avec Mijoté"
      className="fixed inset-0 z-[70] overflow-y-auto bg-background"
    >
      <div className="mx-auto flex max-w-reading flex-col gap-6 px-6 py-8">
        <div className="flex items-start justify-between gap-4">
          <LogoLockup />
          {/* « Passer » vaut « vu » : proposer une sortie qui ferait revenir
              l'écran demain serait une promesse non tenue. */}
          <Button type="button" variant="ghost" size="compact" onClick={onFermer}>
            Passer
          </Button>
        </div>

        <div className="flex flex-col gap-2">
          <h1 className="text-heading-1">Bienvenue</h1>
          <p className="text-lede">
            {/* Pas de nombre : la liste change selon l'appareil — la carte
                d'installation disparaît une fois l'application posée sur
                l'écran d'accueil, celle du raccourci ne vaut que pour un
                iPhone. « Six choses » aurait été faux un jour sur deux. */}
            L’essentiel, et vous n’aurez plus besoin de cet écran. Il reste dans les réglages si vous voulez y
            revenir.
          </p>
        </div>

        <BienDemarrerCartes />

        {/* Pas de « suivant » sur la dernière carte : la phrase d'honnêteté ne
            se saute pas d'un clic accidentel, elle se reconnaît. */}
        <div className="flex justify-end pb-8">
          <Button type="button" variant="primary" onClick={onFermer}>
            <Icon name="coche" size={1.1} />
            J’ai compris
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Le montage : affiché quand la personne ne l'a jamais vu.
 *
 * Fermé **avant** l'appel réseau (`marquerAccueilVu`), et non après : une
 * connexion lente ne doit pas laisser l'écran collé une seconde de plus après
 * la tape. Si l'appel échoue, l'écran reviendra à la prochaine ouverture —
 * c'est le bon repli, et il ne coûte qu'une tape de plus.
 */
export function BienDemarrerAuPremierPas(): React.JSX.Element | null {
  const session = useSession();
  const [ferme, setFerme] = useState(false);

  const membre = session?.member ?? null;
  const partage = session?.device != null;
  if (ferme || membre === null || partage || membre.welcomed_at !== null) return null;

  return (
    <BienDemarrer
      onFermer={() => {
        setFerme(true);
        marquerAccueilVu()
          .then(() => rafraichirSession())
          .catch(() => { /* il reviendra la prochaine fois, et c'est le bon repli */ });
      }}
    />
  );
}
